import { PropsWithChildren, useContext } from 'react';

import ChannelProvider from '@/channels/ChannelProvider';
import { userChannelName } from '@/channels/utils/channelNames';
import AppContext from '@/contexts/appContext';

import { Props } from './types';

const NotificationChannel = ({ children, user }: PropsWithChildren<Props>) => {
  const { currentUser } = useContext(AppContext);
  // Realtime cipher key derives from the stable per-user websocket_key, not the
  // auth token — the token is now a rotating JWT and no longer usable as a
  // shared symmetric secret. Must stay in sync with the backend, which encrypts
  // this user's channel with AffectUser.websocket_key.
  //
  // Backwards-compat: sessions stored in localStorage before websocket_key was
  // added won't have it. Fall back to the legacy api_key material so notifications
  // still decrypt during rollout.
  const keyMaterial = currentUser?.websocket_key || currentUser?.api_key || '';
  const keyBytes = new TextEncoder().encode(keyMaterial);

  // Wait for a full 256-bit key before subscribing. The backend encrypts this
  // user's channel with their websocket_key; subscribing with any other cipher
  // (e.g. ChannelProvider's public default) silently drops messages, and a
  // short/empty key throws in Ably. Until the key loads (next profile/session
  // fetch) render nothing so we never attach with the wrong cipher.
  if (keyBytes.byteLength < 32) {
    return null;
  }

  const options = {
    cipher: {
      algorithm: 'aes',
      key: new Uint8Array(keyBytes.buffer.slice(0, 32)),
      keyLength: 256,
      mode: 'cbc'
    }
  };

  return (
    <ChannelProvider channelName={userChannelName(user?.id || '')} options={options}>
      {children}
    </ChannelProvider>
  );
};

export default NotificationChannel;
